/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable no-plusplus */
/* eslint-disable prefer-const */

// Determine which saturation index to use (LSI or S&DI) for given recovery index
const getSaturationIndex = (value, recoveryIndex) => {
  if (!value) return null;
  const lsi = value?.LSI?.[recoveryIndex];
  if (lsi != null && lsi <= 3 && !Number.isNaN(lsi)) return { key: 'LSI', value: Number(lsi).toFixed(2) };
  const sdi = value?.['S&DI']?.[recoveryIndex];
  if (sdi != null && !Number.isNaN(sdi)) return { key: 'S&DI', value: Number(sdi).toFixed(2) };
  return null;
};

// Compute CCPP display and limit text
const getCCPPAndLimit = (ccppValue, saturationKey, isDualEq) => {
  const keyForLimit = saturationKey || 'LSI';
  const fmt = v => (v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : '-');
  const ccppFmt = fmt(ccppValue);
  if (isDualEq) {
    const limitText = keyForLimit === 'LSI'
      ? 'CCPP < 1000 or LSI < 3'
      : 'CCPP < 1000 or S&DI < 3';
    return { limit: limitText, ccpp: ccppFmt };
  }
  return { limit: '3', ccpp: '' };
};

// Build Calcium Carbonate row
const buildCalciumCarbonateRow = (calciumCarbonateData, recoveryIndex, isDualEq) => {
  const saturationInfo = getSaturationIndex(calciumCarbonateData, recoveryIndex);
  const { limit, ccpp } = getCCPPAndLimit(calciumCarbonateData?.CCPP?.[recoveryIndex], saturationInfo?.key, isDualEq);
  const row = {};
  if (ccpp && ccpp !== '-') row.CCPP = ccpp;
  if (saturationInfo) row[saturationInfo.key] = saturationInfo.value ?? '-';
  row.Limit = limit ?? '';
  return row;
};

// Safely format a numeric dose (or numeric-like string) to 2 decimals; return 'N.A' for null/invalid values.
// Centralizing this avoids repeated guard code and runtime errors when .toFixed is invoked on undefined.
const formatDose = (v) => {
  if (v == null) return 'N.A';
  if (typeof v === 'string' && v.trim().toUpperCase() === 'N.A') return 'N.A';
  const num = Number(v);
  return Number.isFinite(num) ? num.toFixed(2) : 'N.A';
};

// Main transformer
const generateStandAloneArgoData = ({invoiceData}) => {

  const activeDualequationCCPP = JSON.parse(localStorage.getItem('Active_DualequationCCPP')) || {};
  const ro_output = (JSON.parse(localStorage.getItem('ro_ouput_data'))?.Argo?.output_user?.argo_output?.argo) || {};
  const ro_payload = (JSON.parse(localStorage.getItem('ro_payload'))?.ro?.Argo?.argo_dict) || {};
  const isStandaloneArgo = String(localStorage.getItem('StandAloneARGO')).toLowerCase() === 'true';

  if (!invoiceData || !isStandaloneArgo) return invoiceData;

  const payloadWaterSource = ro_payload?.antiscalant;
  const payloadRecovery = ro_payload?.recovery;
  if (payloadWaterSource == null || payloadRecovery == null) return invoiceData;

  const recoveryIndex = payloadRecovery;
  const calciumCarbonateData = ro_output?.calculation_results?.['Calcium Carbonate'];
  const isDualEq = activeDualequationCCPP?.[payloadWaterSource] === 1;
  const caCO3Row = buildCalciumCarbonateRow(calciumCarbonateData, recoveryIndex, isDualEq);
  const ProductName = payloadWaterSource === 'UNTREATED' ? 'UNTREATED' : `Hypersperse ${payloadWaterSource}`;
  console.log('Calcium Carbonate Row:', caCO3Row);
  console.log('Product Name:', ProductName);
  console.log('Payload Recovery:', ro_output);

  try {
    // Filter out any existing Recommended Dose keys from prior transformations
    const plantDataFiltered = Object.fromEntries(
      Object.entries(invoiceData['Plant Data'] || {}).filter(([k]) => !k.startsWith('Recommended Dose of '))
    );
    return {
      ...invoiceData,
      'Plant Data': {
        ...plantDataFiltered,
        'Water Source': ProductName,
        [`Recommended Dose of ${ProductName}`]: formatDose(ro_output?.as_ppm_dose?.[recoveryIndex]?.[payloadWaterSource]),
        Recovery: String(payloadRecovery).includes('%') ? payloadRecovery : `${payloadRecovery}%`
      },
      'Treatment and Summary': {
        ...invoiceData['Treatment and Summary'],
        'Selected Product': ProductName,
        Recovery: String(payloadRecovery).includes('%') ? payloadRecovery : `${payloadRecovery}%`,
        'Feed Dosage': formatDose(ro_output?.as_ppm_dose?.[recoveryIndex]?.[payloadWaterSource]),
        'Conc Dosage': formatDose(ro_output?.as_ppm_brine?.[recoveryIndex]?.[payloadWaterSource])
      },
      'Required Dosage': formatDose(ro_output?.as_ppm_dose?.[recoveryIndex]?.[payloadWaterSource]),
      'Selected Product': ProductName,
      'Calculation Results': {
        ...invoiceData['Calculation Results'],
        '% of Max': {
          ...ro_output?.calculation_results?.['% of Max']?.[recoveryIndex]?.[payloadWaterSource]
        },
        'Limits': {
          ...ro_output?.calculation_results?.Limits?.[payloadWaterSource]
        },
        'Saturation': {
          ...ro_output?.calculation_results?.Saturation?.[recoveryIndex]
        },
        'Calcium Carbonate': {
          ...caCO3Row
        }
      },
      'Degrees of Saturation': {
        // 'No Inhibitor': { ...invoiceData?.['Degrees of Saturation']?.['No Inhibitor'] },
        'No Inhibitor': { ...ro_output?.graph_data_standalone?.[recoveryIndex]?.UNTREATED },
        // Conditionally add the Hypersperse series only when the water source is not UNTREATED
        ...(payloadWaterSource === 'UNTREATED' ? {} : {
          [`With Hypersperse ${payloadWaterSource}`]: {
            ...ro_output?.graph_data_standalone?.[recoveryIndex]?.[payloadWaterSource]
          }
        })
      },
      // Water Quality Data values are left raw (no formatDose) because downstream consumers may
      // require original precision or non-numeric markers; formatting could hide diagnostic states.
      'Water Quality Data': {
        'RO Feed': {
          'Calcium': ro_output?.feed_ppm?.['Ca++'],
          'Magnesium': ro_output?.feed_ppm?.['Mg++'],
          'Sodium': ro_output?.feed_ppm?.['Na+'],
          'Potassium': ro_output?.feed_ppm?.['K+'],
          'Ammonia - N': ro_output?.feed_ppm?.['NH4+'],
          'Barium': ro_output?.feed_ppm?.['Ba++'],
          'Strontium': ro_output?.feed_ppm?.['Sr++'],
          'Iron': ro_output?.feed_ppm?.['Fe++'],
          'Manganese': ro_output?.feed_ppm?.['Mn++'],
          'Sulphate': ro_output?.feed_ppm?.['SO4--'],
          'Chloride': ro_output?.feed_ppm?.['Cl-'],
          'Fluoride': ro_output?.feed_ppm?.['F-'],
          'Nitrate': ro_output?.feed_ppm?.['NO3-'],
          'Bromide': ro_output?.feed_ppm?.['Br-'],
          'Phosphate': ro_output?.feed_ppm?.['PO4---'],
          'Boron': ro_output?.feed_ppm?.['B-'],
          'Silica': ro_output?.feed_ppm?.SiO2,
          'Hydrogen Sulfide': ro_output?.feed_ppm?.H2S,
          'Bicarbonate': ro_output?.feed_ppm?.['HCO3-'],
          'Carbon Dioxide': ro_output?.feed_ppm?.CO2,
          'Carbonate': ro_output?.feed_ppm?.['CO3--'],
          'ph': ro_output?.feed_ppm?.ph,
          'CCPP': ro_output?.feed_ppm?.CCPP,
          'LSI': ro_output?.feed_ppm?.LSI,
          'Ionic Strength': ro_output?.feed_ppm?.['Ionic Strength'],
          'TDS': ro_output?.feed_ppm?.tds
        },
        'Brine': {
          'Calcium': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Ca++'],
          'Magnesium': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Mg++'],
          'Sodium': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Na+'],
          'Potassium': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['K+'],
          'Ammonia - N': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['NH4+'],
          'Barium': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Ba++'],
          'Strontium': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Sr++'],
          'Iron': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Fe++'],
          'Manganese': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Mn++'],
          'Sulphate': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['SO4--'],
          'Chloride': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Cl-'],
          'Fluoride': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['F-'],
          'Nitrate': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['NO3-'],
          'Bromide': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['Br-'],
          'Phosphate': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['PO4---'],
          'Boron': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['B-'],
          'Silica': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.SiO2,
          'Hydrogen Sulfide': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.H2S,
          'Bicarbonate': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['HCO3-'],
          'Carbon Dioxide': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.CO2,
          'Carbonate': ro_output?.waste_dict?.ppm_dict?.[recoveryIndex]?.['CO3--'],
          'ph': ro_output?.waste_dict?.ph_dict?.[recoveryIndex],
          'CCPP': ro_output?.waste_dict?.ccpp_dict?.[recoveryIndex],
          'LSI': ro_output?.waste_dict?.lsi_dict?.[recoveryIndex],
          'Ionic Strength': ro_output?.waste_dict?.ionicstr_dict?.[recoveryIndex],
          'TDS': ro_output?.waste_dict?.tds_dict?.[recoveryIndex]
        }
      }
    };
  } catch (e) {
    console.warn('generateStandAloneArgoData failed, returning original invoiceData', e);
    return invoiceData;
  }
};

export { generateStandAloneArgoData };
export default generateStandAloneArgoData;
